/**
 * Tableau de bord de pilotage (bible §17) — contrat BFF ↔ front.
 *
 * Trois principes non négociables, hérités de §17.4/§17.5 :
 *  1. AUCUN montant. Décision Dove du 2026-07-30 : la page compte, elle ne
 *     chiffre pas. Le chiffre d'affaires n'y figure sous aucune forme, même
 *     dérivée — les euros vivent dans Finance et la Balance âgée.
 *  2. Tout nombre est CLIQUABLE : chaque bloc porte le lien qui ouvre
 *     exactement les éléments qu'il compte.
 *  3. Tout nombre est REPRODUCTIBLE : il vient d'un compteur documenté, jamais
 *     d'une estimation ni d'un modèle de langage.
 */
import { z } from "zod";
import { SyncMetaSchema } from "./dto";
import { EtatSaisieSchema } from "./pauses";
import type { Role } from "./roles";

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date ISO attendue");

/* ------------------------------------------------------------ conseils */

/** Trois niveaux seulement : §17.4 interdit le rouge pour une simple information. */
export const GRAVITES = ["critique", "attention", "info"] as const;
export type Gravite = (typeof GRAVITES)[number];

export const ConseilSchema = z.object({
  /** identifiant stable de la règle — permet de tester et de suivre un conseil */
  regle: z.string().min(1),
  gravite: z.enum(GRAVITES),
  /** phrase d'action, éventuellement reformulée par le modèle — SANS chiffre */
  action: z.string().min(1),
  /** le fait chiffré, TOUJOURS construit par le serveur à partir des compteurs */
  fait: z.string().min(1),
  lien: z.string().min(1),
  lienLibelle: z.string().min(1),
});
export type Conseil = z.infer<typeof ConseilSchema>;

/** Nombre de conseils affichés — au-delà, ce n'est plus un conseil, c'est une liste. */
export const CONSEILS_AFFICHES = 3;

/* ---------------------------------------------------------------- tuiles */

/**
 * Un morceau d'une tuile agrégée, avec SON lien.
 *
 * Né du seul défaut de §17.5 qu'un simple recomptage ne pouvait pas corriger :
 * « Actions dues » additionne trois files indépendantes, et « Messages à
 * traiter » additionne deux univers (fenêtres WhatsApp et e-mails à
 * rattacher). Aucun écran n'ouvre cette somme — le clic menait donc toujours
 * à un nombre plus petit que celui affiché.
 *
 * La réponse n'est pas de casser l'agrégat, qui a du sens le matin, mais de
 * l'accompagner de ses parts : chaque part porte le nombre qu'elle ouvre, et
 * la somme des parts vaut exactement `valeur`. Le total, lui, cesse d'être
 * cliquable — un nombre qui ne peut rien ouvrir ne doit pas ressembler à un
 * lien.
 */
export const TuilePartSchema = z.object({
  libelle: z.string(),
  valeur: z.number().int().min(0),
  lien: z.string(),
});
export type TuilePart = z.infer<typeof TuilePartSchema>;

export const TuileSchema = z.object({
  id: z.string(),
  libelle: z.string(),
  valeur: z.number().int().min(0),
  /** seconde ligne : « dont 3 en retard » — jamais un pourcentage sans dénominateur (§17.4) */
  precision: z.string().nullable(),
  ton: z.enum(["neutre", "attention", "critique"]),
  lien: z.string(),
  /** §17.5 : une source obsolète affiche un avertissement SUR le KPI */
  perime: z.boolean(),
  /**
   * Vide pour une tuile dont le nombre ouvre une liste unique. Non vide, la
   * tuile n'est plus cliquable et ses parts le deviennent. `.default([])` :
   * une réponse d'un serveur antérieur reste valide (règle du 11/08 — le
   * contrat accepte la forme AVANT que le serveur ne l'émette).
   */
  repartition: z.array(TuilePartSchema).default([]),
});
export type Tuile = z.infer<typeof TuileSchema>;

/** Au-delà, la donnée servie n'est plus « de ce matin » et doit le dire. */
export const FRAICHEUR_MAX_SECONDES = 45 * 60;

/* ------------------------------------------------------------ mon jour */

export const MonJourSchema = z.object({
  dues: z.number().int().min(0),
  enRetard: z.number().int().min(0),
  attenteTiers: z.number().int().min(0),
  saisiesAConfirmer: z.number().int().min(0),
  clotureesAujourdhui: z.number().int().min(0),
  /** actions ouvertes qui n'ont encore aucun responsable — le vrai angle mort */
  nonAttribuees: z.number().int().min(0),
});
export type MonJour = z.infer<typeof MonJourSchema>;

/* ----------------------------------------------------------- production */

/**
 * Répartition des travaux en cours par ANCIENNETÉ, et non par atelier.
 *
 * Pourquoi : mesuré sur les données réelles le 2026-07-30, les 154 travaux en
 * cours portent TOUS le statut `RECU` — la synchronisation Vevidental ne
 * transmet jamais l'atelier courant (CFAO, Céramique…), elle passe directement
 * à `EXPEDIE`. Une barre « par secteur » afficherait donc un bloc unique et
 * ferait croire à une information qui n'existe pas. L'ancienneté, elle, est
 * réelle (date de réception) et dit la même chose utile : ce qui traîne.
 */
export const TRANCHES_ANCIENNETE = [
  { id: "j0_3", libelle: "3 jours ou moins", max: 3 },
  { id: "j4_7", libelle: "4 à 7 jours", max: 7 },
  { id: "j8_14", libelle: "8 à 14 jours", max: 14 },
  { id: "j15_30", libelle: "15 à 30 jours", max: 30 },
  { id: "j30p", libelle: "plus de 30 jours", max: Number.POSITIVE_INFINITY },
] as const;
export type TrancheAnciennete = (typeof TRANCHES_ANCIENNETE)[number]["id"];

export function trancheDe(jours: number): TrancheAnciennete {
  return (TRANCHES_ANCIENNETE.find((t) => jours <= t.max) ?? TRANCHES_ANCIENNETE[4]).id;
}

export const JourChargeSchema = z.object({
  jour: IsoDate,
  libelle: z.string(), // « jeu 31 »
  ouvre: z.boolean(),
  motifFermeture: z.string().nullable(), // « férié », « fermeture GéCéram »
  nombre: z.number().int().min(0),
  /** au-dessus de la charge habituelle — signalé, jamais interprété */
  surcharge: z.boolean(),
});
export type JourCharge = z.infer<typeof JourChargeSchema>;

export const ProductionSchema = z.object({
  enCours: z.array(
    z.object({
      tranche: z.enum(TRANCHES_ANCIENNETE.map((t) => t.id) as [TrancheAnciennete, ...TrancheAnciennete[]]),
      libelle: z.string(),
      nombre: z.number().int().min(0),
    }),
  ),
  total: z.number().int().min(0),
  /**
   * les 5 prochains jours civils, fermetures comprises (elles se voient) —
   * ≠ `risquesParJour`, qui ne montre plus le week-end (18/09). Plus dessiné
   * par l'écran ; nourrit le conseil « surcharge du jour ».
   */
  aLivrer: z.array(JourChargeSchema),
  /** médiane des échéances quotidiennes des 8 dernières semaines ouvrées */
  chargeHabituelle: z.number().nullable(),
  /** honnêteté §7.B : sans échéance ≠ en retard — le nombre est montré */
  sansEcheance: z.number().int().min(0),
  echeancesDepassees: z.number().int().min(0),
  /** dépassées de plus de 30 jours : presque toujours un statut oublié dans Vevidental */
  echeancesTresAnciennes: z.number().int().min(0),
});
export type Production = z.infer<typeof ProductionSchema>;

/* ------------------------------------------------- à livrer aujourd'hui */

/**
 * L'état de livraison Courses d'un travail du jour.
 *
 * v1 : le serveur émet TOUJOURS null — Courses ne pousse encore AUCUNE donnée
 * opérationnelle vers le CRM (iframe seule + pont fichiers, vérifié le 27/08).
 * Le schéma réserve la place pour que le front affiche « inconnue » sans
 * jamais DÉDUIRE un état ; la vraie situation viendra quand Courses poussera
 * une route interne (patron fichiers-courses.ts). Rien n'est estimé.
 */
export const LivraisonCoursesSchema = z.object({
  etat: z.enum([
    "livree_signee",
    "expediee_non_confirmee",
    "tournee_reservee",
    "coursier_non_assigne",
    "retrait_sur_place",
  ]),
  horodatage: z.string().datetime({ offset: true }).nullable(),
  detail: z.string().nullable(),
});
export type LivraisonCourses = z.infer<typeof LivraisonCoursesSchema>;

/** Le blocage porté par l'action ouverte du même travail — jamais inventé. */
export const BlocageLigneSchema = z.object({
  actionId: z.string(),
  categorie: z.string(),
  niveau: z.string(),
  detail: z.string().nullable(),
});
export type BlocageLigne = z.infer<typeof BlocageLigneSchema>;

export const LigneALivrerSchema = z.object({
  workId: z.string(),
  workCode: z.string(),
  cabinetId: z.string().nullable(),
  cabinetNom: z.string().nullable(),
  praticienNom: z.string().nullable(),
  /** initiales SEULES, dérivées côté BFF — le nom complet ne traverse jamais */
  patientInitiales: z.string().nullable(),
  /** famille dominante (partage/type-travail.ts), null si aucune ligne produit */
  typeTravail: z.string().nullable(),
  echeance: IsoDate,
  /**
   * `saisie_crm` n'existe pas ici : les dates saisies sur cartes WORK
   * manuelles ne sont pas rapprochables d'un travail de façon fiable
   * (relances.ts:428) — le conflit de dates est REPORTÉ, le littéral réserve
   * la place.
   */
  provenanceDate: z.literal("vevidental"),
  /** status_name brut de la source — jamais interprété */
  statutConnu: z.string().nullable(),
  blocage: BlocageLigneSchema.nullable(),
  responsable: z.string().nullable(),
  livraison: LivraisonCoursesSchema.nullable(),
});
export type LigneALivrer = z.infer<typeof LigneALivrerSchema>;

/* -------------------------------------------------------------- risques */

export const RisqueItemSchema = z.object({
  actionId: z.string(),
  workCode: z.string(),
  cabinetNom: z.string(),
  categorie: z.string(),
  niveau: z.string(),
  echeance: IsoDate,
  enRetard: z.boolean(),
});
export type RisqueItem = z.infer<typeof RisqueItemSchema>;

export const JourRisquesSchema = z.object({
  jour: IsoDate,
  libelle: z.string(),
  /**
   * Décidé par le SERVEUR (qui connaît « today »), pas par le rang de la
   * colonne : depuis que le week-end n'est plus montré (18/09), un samedi la
   * première colonne est lundi — elle n'est pas « aujourd'hui ». Défaut false :
   * un serveur antérieur ne l'émet pas.
   */
  estAujourdhui: z.boolean().default(false),
  ouvre: z.boolean(),
  motifFermeture: z.string().nullable(),
  items: z.array(RisqueItemSchema),
});
export type JourRisques = z.infer<typeof JourRisquesSchema>;

/* ------------------------------------------------------------- relation */

export const SilenceSchema = z.object({
  cabinetId: z.string(),
  cabinetNom: z.string(),
  praticienNom: z.string().nullable(),
  silenceJours: z.number().int().min(0),
  cadenceJours: z.number().positive(),
  ratio: z.number().min(0),
});
export type Silence = z.infer<typeof SilenceSchema>;

export const RelationSchema = z.object({
  segments: z.object({
    actif: z.number().int().min(0),
    tiede: z.number().int().min(0),
    silencieux: z.number().int().min(0),
    dormant: z.number().int().min(0),
    inactif: z.number().int().min(0),
  }),
  silences: z.array(SilenceSchema),
  /** cabinets silencieux qui ont renvoyé un travail dans les 7 derniers jours */
  reprises7j: z.number().int().min(0),
  vipSilencieux: z.number().int().min(0),
});
export type Relation = z.infer<typeof RelationSchema>;

/* ------------------------------------------------------- communications */

export const CommunicationsSchema = z.object({
  /** brouillons sortants écrits par le CRM, à relire : la file de validation de /emails (R1) */
  brouillonsAValider: z.number().int().min(0).default(0),
  /** fenêtres de service WhatsApp qui se ferment dans moins de 6 h */
  fenetresExpirantes: z.number().int().min(0),
  conversationsOuvertes: z.number().int().min(0),
  emailsAValider: z.number().int().min(0),
  appelsAujourdhui: z.number().int().min(0),
});
export type Communications = z.infer<typeof CommunicationsSchema>;

/* -------------------------------------------------- santé & charge (admin) */

export const SanteItemSchema = z.object({
  id: z.string(),
  nom: z.string(),
  etat: z.enum(["a_jour", "retarde", "obsolete", "indisponible", "non_configure"]),
  detail: z.string(),
});
export type SanteItem = z.infer<typeof SanteItemSchema>;

/** Charge = volume de travail ouvert. §17.5 : ce n'est PAS un indicateur de performance. */
export const ChargeItemSchema = z.object({
  responsable: z.string().nullable(), // null = non attribuée
  ouvertes: z.number().int().min(0),
  enRetard: z.number().int().min(0),
});
export type ChargeItem = z.infer<typeof ChargeItemSchema>;

/* -------------------------------------------------------------- réponse */

export const TableauBordResponseSchema = z.object({
  bonjour: z.string(),
  jour: IsoDate,
  conseils: z.array(ConseilSchema),
  /** false = repli sur le texte des règles (pas de modèle, ou modèle indisponible) */
  conseilsRediges: z.boolean(),
  monJour: MonJourSchema,
  /**
   * État de la saisie Vevidental. Quand elle est en pause, la page compte des
   * choses qui n'ont pas encore été saisies : elle doit le DIRE plutôt que de
   * laisser croire à des chiffres complets (§17.5, principe d'honnêteté).
   */
  saisie: EtatSaisieSchema,
  tuiles: z.array(TuileSchema),
  production: ProductionSchema,
  relation: RelationSchema,
  communications: CommunicationsSchema,
  charge: z.array(ChargeItemSchema).nullable(),
  sync: SyncMetaSchema,
  /*
   * Sprint 3 — tableau de bord v2. Tous en `.default()` : une réponse d'un
   * serveur antérieur reste valide (règle du 11/08, comme `repartition`).
   */
  aLivrerAujourdhui: z.array(LigneALivrerSchema).default([]),
  risquesParJour: z.array(JourRisquesSchema).default([]),
  /** honnêteté §7.B : sans échéance ≠ sans risque — le nombre est montré à part */
  risquesSansEcheance: z.number().int().min(0).default(0),
  /** dernier travail ENTRÉ dans la copie — affiché en permanence (fraîcheur) */
  dernierTravailRecuLe: IsoDate.nullable().default(null),
  /** jours ouvrés de silence de la réception ; null = aucun travail sur la fenêtre */
  receptionSilencieuseJoursOuvres: z.number().int().min(0).nullable().default(null),
});
export type TableauBordResponse = z.infer<typeof TableauBordResponseSchema>;

/* ----------------------------------------------------------------- RBAC */

/** Blocs de direction (§17.2) : santé des intégrations et charge par responsable. */
export function canVoirDirection(roles: readonly Role[]): boolean {
  return roles.includes("admin");
}
